/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import React, { useEffect, useState } from "react";
import { View } from "react-native";
import { useLazyQuery } from "@apollo/client";

import { useSelector } from "react-redux";
import { Redirect } from "react-router-dom";
import { RootState, SelfObject } from "store/types";

import { Button, LoadingView, Text, styles } from "UpduoLibrary";
import {
  skalePrimaryBlue,
  skaleWhite,
  skaleWebTextLightGray,
  colorWithOpacity,
  upduoPrimaryBlack,
} from "config/UpduoColors";
import AnnouncementDetail from "./AnnouncementDetail";
import EmptyCalendarSVG from "assets/empty-calendar.svg";
import { GET_STAFF_PERIODICTASK_LIST } from "graphql/internal/Queries";
import { GetStaffPeriodicTaskListQuery } from "graphql/internal/generated";
import * as UpduoAnalytics from "config/UpduoAnalytics";

export default function AnnouncementsRoute() {
  const selfObject: SelfObject = useSelector(
    (state: RootState) => state.selfObject
  );
  const authToken = useSelector((state: RootState) => state.authToken);

  const [announcementsList, setAnnouncementsList] = useState<any[]>([]);
  const [showNewAnnouncement, setShowNewAnnouncement] = useState(false);

  const [getPeriodicTasks, { loading: isLoading }] = useLazyQuery(
    GET_STAFF_PERIODICTASK_LIST,
    {
      fetchPolicy: "network-only",
      onCompleted: (data: GetStaffPeriodicTaskListQuery) => {
        let newAnnouncementsList =
          data?.staffPeriodictasks?.edges?.map((edge) => {
            const kwargsObject = JSON.parse(edge?.node?.kwargs || "{}");
            return {
              id: edge?.node?.id,
              clockedTime: edge?.node?.clocked?.clockedTime,
              heading: kwargsObject.heading,
              content: kwargsObject.content,
              mode: kwargsObject.mode ? kwargsObject.mode : "PUSH",
            };
          }) || [];
        newAnnouncementsList = newAnnouncementsList
          .filter((announcement) => {
            if (!announcement.clockedTime) {
              return true;
            }
            const clocked = new Date(announcement.clockedTime);
            const now = new Date();
            return clocked.getTime() > now.getTime();
          })
          .sort((a, b) => {
            if (!a.clockedTime || !b.clockedTime) {
              return 0;
            }
            const aClocked = new Date(a.clockedTime);
            const bClocked = new Date(b.clockedTime);
            return aClocked.getTime() - bClocked.getTime();
          });
        setAnnouncementsList(newAnnouncementsList);
        setShowNewAnnouncement(false);
      },
      onError: (error) => {
        UpduoAnalytics.logEvent("app-announcements-getperiodictasks-error", {
          error,
        });
      },
    }
  );

  useEffect(() => {
    getPeriodicTasks();
  }, [authToken]);

  if (!selfObject.isStaff) {
    return <Redirect to={"/"} />;
  }

  const userActionShield = (
    <View
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: window.innerHeight,
        backgroundColor: colorWithOpacity(skaleWhite, 0.7),
        zIndex: 15,
      }}
    >
      <LoadingView topSpacing={150} />
    </View>
  );
  return (
    <View style={{ flex: 1 }}>
      <View style={[styles.routeBodyContainer, { zIndex: 10 }]}>
        <View
          style={{
            zIndex: 5,
            paddingVertical: 10,
            justifyContent: "center",
          }}
        >
          <View
            style={{
              flexDirection: "row",
              alignItems: "flex-start",
              paddingHorizontal: 15,
              justifyContent: "space-between",
            }}
          >
            <View style={{ alignSelf: "center", justifyContent: "center" }}>
              <Text
                style={{
                  fontSize: 24,
                  fontWeight: "bold",
                  color: upduoPrimaryBlack,
                }}
              >
                {"Notifications"}
              </Text>
            </View>
            {showNewAnnouncement ? null : (
              <Button
                width={250}
                onPress={() => {
                  UpduoAnalytics.logEvent("user-announcement-new", {
                    isClone: false,
                  });
                  setShowNewAnnouncement(true);
                }}
                text={"+ New Notification"}
                height={36}
                borderColor={skalePrimaryBlue}
              />
            )}
          </View>
        </View>
        {showNewAnnouncement || announcementsList.length > 0 ? (
          announcementsList.map((announcement) => (
            <AnnouncementDetail
              key={announcement.id}
              announcement={announcement}
              onRefresh={getPeriodicTasks}
            />
          ))
        ) : (
          <View
            style={{
              alignItems: "center",
              marginTop: 20,
              paddingTop: 10,
            }}
          >
            <img
              src={EmptyCalendarSVG}
              style={{ width: 300 }}
              alt={"empty calendar"}
            />
            <Text style={{ color: skaleWebTextLightGray }}>
              {"No upcoming notifications; create a new one!"}
            </Text>
          </View>
        )}
        {showNewAnnouncement ? (
          <AnnouncementDetail
            announcement={{
              id: "new",
              heading: "New Notification Heading",
              content: "New Notification Content",
              mode: "PUSH",
            }}
            isNew={true}
            onRefresh={getPeriodicTasks}
            onDiscardNew={() => {
              setShowNewAnnouncement(false);
            }}
          />
        ) : null}
        {isLoading ? userActionShield : null}
      </View>
    </View>
  );
}
